import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';

function buildEmptyForm() {
  return {
    title: '',
    details: '',
    discoveredAt: '',
  };
}

function toIsoDateTime(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return null;
  }

  return new Date(raw).toISOString();
}

export function useComplianceBreach() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState(buildEmptyForm());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await adminApi.getBreachIncidents(token);
      setItems(Array.isArray(response?.items) ? response.items : []);
    } catch (requestError) {
      setItems([]);
      setError(requestError.message || 'Failed to load breach incidents');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const submitCreate = useCallback(async (event) => {
    event.preventDefault();

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await adminApi.createBreachIncident(token, {
        title: String(form.title || '').trim() || null,
        details: String(form.details || '').trim() || null,
        discoveredAt: toIsoDateTime(form.discoveredAt),
      });
      setForm(buildEmptyForm());
      setSuccess('Breach incident created.');
      await load();
    } catch (requestError) {
      setError(requestError.message || 'Failed to create breach incident');
    } finally {
      setIsSubmitting(false);
    }
  }, [form.details, form.discoveredAt, form.title, load, token]);

  return {
    items,
    isLoading,
    isSubmitting,
    form,
    error,
    success,
    onFormChange,
    submitCreate,
  };
}
